import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Menu, X } from "lucide-react";
import { api } from "@/lib/api";
import { LeadMagnetSection } from "@/components/LeadMagnetSection";
import { ExitIntentGuide } from "@/components/ExitIntentGuide";
import { FAQSection } from "@/components/FAQSection";
import { FenyxSection } from "@/components/FenyxSection";
import { ASSETS, PHOTOS, BRAND, TRANSFORMATIONS } from "@/lib/brand";

const INK = "#141311";
const BRONZE = "#A89880";
const MUTED = "#8A8071";
const LINEN = "#EFE9E2";

const METHOD = [
  { num: "01", title: "Listen deeply", body: "Your symptoms are not flaws — they're signals. We begin by hearing what your body has been saying all along." },
  { num: "02", title: "Test, don't assume", body: "Functional lab work reveals what standard bloodwork misses — hormones, gut health, metabolic function, and more." },
  { num: "03", title: "Restore from within", body: "A fully personalized protocol built around your biology — sustainable, precise, and designed to last." },
];

const PILLARS = [
  { num: "01", title: "MIND", body: "Mindset, identity, nervous system regulation — the inner work that makes the outer change last." },
  { num: "02", title: "BODY", body: "Metabolic training, functional nutrition, hormone-friendly fitness — built for the long arc." },
  { num: "03", title: "SOUL", body: "Self-awareness, self-compassion, integration. Wellness that nourishes more than the scale." },
];

const REASONS = [
  "Your metabolism feels sluggish — what worked in your 20s and 30s suddenly doesn't.",
  "You're doing hours of cardio, eating 1,200 calories or less — and the scale won't budge.",
  "You're stuck in cycles of yo-yo dieting, bloating, or feeling like your body is working against you.",
  "You've lost the motivation — and you're not sure who you are without the next diet.",
];

const PHASES = [
  { num: "01", title: "DETOX", weeks: "Weeks 1–4", body: "Not a starve-you juice cleanse. A researched, science-backed protocol that leaves you full, nourished, and energized — while detoxing at a cellular level and dropping the inflammation that's making your body resistant to change.", tags: ["Cellular Detox", "Inflammation", "Liver Support"] },
  { num: "02", title: "RESTORE", weeks: "Weeks 5–8", body: "We phase out of detox and focus on healing the gut, balancing blood sugar, lowering cortisol, and restoring the metabolism — so your body feels safe enough for the next phase.", tags: ["Gut Healing", "Blood Sugar", "Cortisol", "Metabolic Safety"] },
  { num: "03", title: "BURN", weeks: "Weeks 9–20", body: "Your body is out of survival and balanced. Now we move into a dialed-in protocol for strategic fat loss — losing body fat, keeping the muscle, that lean, toned, beautiful look.", tags: ["Strategic Fat Loss", "Strength", "Body Composition", "Identity Shift"] },
  { num: "04", title: "RESET", weeks: "Weeks 21–24", body: "This phase is NEVER skipped. We reverse diet to maintain your results, phase out of deficit, and walk you into a balanced, thriving life without guilt or extremes. The whole point — sustainability.", tags: ["Reverse Dieting", "Metabolic Freedom", "Long-Term Integration"] },
];

const RISE_BULLETS = [
  "Functional lab testing (HTMA, hormones, gut, bloodwork)",
  "Fully customized nutrition & training protocols",
  "Weekly 1:1 check-ins with DJ",
  "Direct in-app messaging with DJ",
  "Custom supplement, sleep & nervous system protocols",
  "Real-time program adjustments based on biofeedback",
];

const MTM_BULLETS = [
  "Four-phase, 24-week structured program",
  "Detox · Restore · Burn · Reset methodology",
  "Group coaching calls + community support",
  "Nutrition, training & mindset curriculum",
  "Reverse diet & metabolic freedom in Phase 4",
];

const QUALS = [
  { title: "Certified Functional Nutrition Coach", body: "Root-cause, systems-based nutrition therapy for lasting healing." },
  { title: "Functional Lab & Testing", body: "Advanced interpretation of hormone, metabolic, and gut markers to build precision protocols." },
  { title: "Women's Hormone & Metabolic Health Specialist", body: "Specialized training in the female endocrine system across all life stages — cycles, perimenopause, and beyond." },
];

const NavLinks = ({ onNavigate }) => (
  <>
    <a href="#approach" onClick={onNavigate} data-testid="nav-approach" className="lp-label-sm hover:opacity-60 transition-opacity">Approach</a>
    <a href="#the-coach" onClick={onNavigate} data-testid="nav-coach" className="lp-label-sm hover:opacity-60 transition-opacity">The Coach</a>
    <a href="#two-paths" onClick={onNavigate} data-testid="nav-offerings" className="lp-label-sm hover:opacity-60 transition-opacity">Programs</a>
    <a href="#results" onClick={onNavigate} data-testid="nav-results" className="lp-label-sm hover:opacity-60 transition-opacity">Results</a>
    <a href={BRAND.calendlyUrl} target="_blank" rel="noreferrer" data-testid="nav-book-call" className="lp-label-sm" style={{ color: BRONZE }}>Book a Call</a>
  </>
);

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [seats, setSeats] = useState(null);
  useEffect(() => {
    api.get("/public/mtm-seats").then((r) => setSeats(r.data)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen lp-body" style={{ background: "#FFFFFF", color: INK }}>
      <ExitIntentGuide />
      {/* ═══ Sticky nav ═══ */}
      <header className="sticky top-0 z-40 border-b"
        style={{ background: "rgba(255,255,255,0.94)", backdropFilter: "blur(12px)", borderColor: "rgba(20,19,17,0.07)" }}>
        <div className="px-5 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-baseline gap-2">
            <span className="font-script" style={{ fontSize: "2rem", color: INK }}>{BRAND.name}</span>
            <span className="lp-label-sm" style={{ color: BRONZE }}>{BRAND.tag}</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8" style={{ color: INK }}>
            <NavLinks />
          </nav>
          <button className="md:hidden p-2 -mr-2" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu" data-testid="mobile-menu-btn" style={{ color: INK }}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <nav className="md:hidden flex flex-col gap-6 px-6 pb-8 pt-2 border-t" data-testid="mobile-menu"
            style={{ color: INK, borderColor: "rgba(20,19,17,0.07)", background: "rgba(255,255,255,0.98)" }}>
            <NavLinks onNavigate={() => setMenuOpen(false)} />
          </nav>
        )}
      </header>

      {/* ═══ Hero ═══ */}
      <section className="grid grid-cols-1 lg:grid-cols-2">
        <div className="flex items-center px-5 md:px-16 lg:px-24 py-14 lg:py-28 order-2 lg:order-1">
          <div className="max-w-xl">
            <div className="lp-rule mb-6 fade-up" />
            <p className="lp-label-sm fade-up" style={{ color: BRONZE }}>Functional Nutrition · Women's Wellness · Root Cause Method</p>
            <h1 className="lp-h mt-6 md:mt-8 fade-up delay-1" style={{ fontSize: "clamp(2.8rem, 6.2vw, 6.2rem)" }}>
              Your body<br />is not broken.
            </h1>
            <div className="mt-2 fade-up delay-2">
              <span className="lp-it" style={{ fontSize: "clamp(2rem, 4.4vw, 4.4rem)", color: BRONZE }}>It is waiting</span>
              <div className="lp-it" style={{ fontSize: "clamp(1.3rem, 2vw, 1.9rem)", color: INK }}>to be understood.</div>
            </div>
            <p className="mt-7 text-base md:text-xl leading-relaxed fade-up delay-3" style={{ color: MUTED }}>
              A premium coaching home for women ready to heal their metabolism, regulate
              their nervous system, and become unrecognizable — <em>mind, body &amp; soul.</em>
            </p>
            <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 fade-up delay-3">
              <a href="#two-paths" className="lp-btn lp-btn-fill w-full sm:w-auto" data-testid="hero-find-path">Find Your Path</a>
              <a href={BRAND.calendlyUrl} target="_blank" rel="noreferrer" className="lp-btn lp-btn-outline w-full sm:w-auto" data-testid="hero-book-call">Book a Discovery Call</a>
            </div>
          </div>
        </div>
        <div className="relative min-h-[48vh] lg:min-h-[88vh] order-1 lg:order-2">
          <img src={PHOTOS.authoritySuit} alt="DJ — Wellness Way with DJ" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
        </div>
      </section>

      {/* ═══ The Method ═══ */}
      <section id="approach" className="px-6 md:px-12 py-16 md:py-28 max-w-[1200px] mx-auto" data-testid="method-section">
        <p className="lp-label-sm" style={{ color: BRONZE }}>The Method ·</p>
        <h2 className="lp-h mt-5 text-4xl md:text-5xl" style={{ maxWidth: 640 }}>
          We don't guess.<br />
          <span className="lp-it normal-case" style={{ color: BRONZE }}>We investigate, then heal.</span>
        </h2>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14">
          {METHOD.map((m) => (
            <div key={m.num} className="pt-8 border-t" style={{ borderColor: "rgba(20,19,17,0.12)" }} data-testid={`method-${m.num}`}>
              <span className="lp-label-sm" style={{ color: BRONZE }}>{m.num}</span>
              <h3 className="lp-h text-2xl mt-4 normal-case" style={{ textTransform: "none" }}>{m.title}</h3>
              <p className="mt-4 leading-relaxed" style={{ color: MUTED }}>{m.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ Philosophy — full-bleed ═══ */}
      <section className="relative py-20 md:py-32 px-6 md:px-12 overflow-hidden" data-testid="philosophy-section">
        <img src={PHOTOS.athleticBack} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "rgba(28,26,23,0.62)" }} />
        <div className="relative max-w-[1100px] mx-auto text-center" style={{ color: "#FFFFFF" }}>
          <p className="lp-label-sm" style={{ color: "#D8D2C6" }}>The Philosophy</p>
          <h2 className="lp-h mt-6 text-5xl md:text-7xl" style={{ color: "#FFFFFF" }}>
            Mind · Body <span className="lp-it" style={{ color: "#FFFFFF" }}>&amp;</span> Soul
          </h2>
          <div className="lp-rule mx-auto mt-10" />
          <p className="lp-it mt-10 text-2xl md:text-3xl max-w-xl mx-auto leading-relaxed" style={{ color: "#EDE8DE" }}>
            Three pillars. One you — restored to her natural state of strength, softness, and undeniable presence.
          </p>
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-10 text-left">
            {PILLARS.map((p) => (
              <div key={p.num} className="pt-8 border-t" style={{ borderColor: "rgba(255,255,255,0.28)" }} data-testid={`pillar-${p.title.toLowerCase()}`}>
                <span className="lp-label-sm" style={{ color: "#CFC7B8" }}>{p.num}</span>
                <h3 className="lp-h text-3xl mt-4" style={{ color: "#FFFFFF" }}>{p.title}</h3>
                <p className="lp-it mt-4 text-lg leading-relaxed" style={{ color: "#E4DED2" }}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Maybe you're here because ═══ */}
      <section className="px-6 md:px-12 py-16 md:py-28 max-w-[1000px] mx-auto" data-testid="reasons-section">
        <p className="lp-label-sm" style={{ color: BRONZE }}>For the Woman Who Knows</p>
        <h2 className="lp-h mt-5 text-4xl md:text-5xl">
          Maybe you're<br />
          <span className="lp-it normal-case" style={{ color: BRONZE }}>here because...</span>
        </h2>
        <div className="mt-14 space-y-0">
          {REASONS.map((r, i) => (
            <div key={r.slice(0, 40)} className="flex items-start gap-8 py-7 border-t" style={{ borderColor: "rgba(20,19,17,0.1)" }} data-testid={`reason-${i + 1}`}>
              <span className="lp-label-sm flex-shrink-0 mt-1" style={{ color: BRONZE }}>0{i + 1}</span>
              <p className="text-lg md:text-xl leading-relaxed" style={{ color: "#4A453D" }}>{r}</p>
            </div>
          ))}
        </div>
        <blockquote className="lp-it mt-16 text-2xl md:text-3xl text-center max-w-2xl mx-auto leading-relaxed" style={{ color: MUTED }}>
          Whatever brought you here — you are in the right place. None of this is your fault.
          You've just been handed the wrong information for a body that has changed.
        </blockquote>
      </section>

      {/* ═══ Meet your coach ═══ */}
      <section id="the-coach" className="px-6 md:px-12 py-16 md:py-28" style={{ background: LINEN }} data-testid="coach-section">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div className="p-3" style={{ border: "1px solid rgba(20,19,17,0.18)" }}>
            <img src={PHOTOS.authorityStool} alt="DJ — Wellness Way with DJ" className="w-full aspect-[4/5] object-cover object-top" />
          </div>
          <div>
            <p className="lp-label-sm" style={{ color: BRONZE }}>Meet your coach.</p>
            <h2 className="lp-h mt-5 text-5xl md:text-6xl">I'm DJ.</h2>
            <p className="lp-it text-3xl md:text-4xl mt-2" style={{ color: BRONZE }}>I was once exactly where you are.</p>
            <p className="lp-label-sm mt-8" style={{ color: MUTED, lineHeight: 1.9 }}>
              Certified Hormone Health Coach · Functional Health Coach · Elite Personal Trainer
            </p>
            <div className="mt-8 space-y-5 text-lg leading-relaxed" style={{ color: "#4A453D" }}>
              <p>Even with my background in personal training and sports nutrition, I was lost, ashamed, and hopeless when my body changed on me — almost overnight — when I turned 30.</p>
              <p>Nothing that worked before worked anymore. Low-carb diets, HIIT, two-a-days — they left me craving sugar, exhausted, inflamed, and gaining weight no matter what I did.</p>
              <p>It wasn't until I discovered functional health — the role of hormones and gut health — that I realized the key wasn't pushing harder. <em className="lp-it text-xl">It was healing.</em> I dove into functional nutrition and hormone science to repair my own metabolism. Then I built the Metabolic Transformation Method to walk other women through the exact same restoration.</p>
            </div>
            <p className="lp-label-sm mt-10" style={{ color: BRONZE }}>— DJ · Wellness Way with DJ</p>
          </div>
        </div>
      </section>

      {/* ═══ Testimonial quote ═══ */}
      <section className="px-6 md:px-12 py-16 md:py-28 text-center max-w-[900px] mx-auto" data-testid="quote-section">
        <p className="lp-label-sm" style={{ color: BRONZE }}>Client Transformation · Real Results</p>
        <blockquote className="lp-it mt-10 text-3xl md:text-4xl leading-snug" style={{ color: INK }}>
          "I went from barely functioning to the most energized, clear, and confident version of myself."
        </blockquote>
        <p className="lp-label-sm mt-8" style={{ color: MUTED }}>— Taylor R. · Rise Within Client</p>
      </section>

      {/* ═══ Two Paths ═══ */}
      <section id="two-paths" className="px-6 md:px-12 py-16 md:py-28 max-w-[1200px] mx-auto" data-testid="two-paths-section">
        <div className="text-center">
          <p className="lp-label-sm" style={{ color: BRONZE }}>The Offerings</p>
          <h2 className="lp-h mt-5 text-5xl md:text-6xl">Two paths.</h2>
          <p className="lp-it text-3xl md:text-4xl mt-2" style={{ color: BRONZE }}>One restoration.</p>
          <div className="lp-rule mx-auto mt-8" />
          <p className="lp-it mt-8 text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed" style={{ color: MUTED }}>
            Whether you walk with a community of women through the Method, or step into bespoke
            1:1 care — every door leads to the same quiet revolution.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* RISE WITHIN */}
          <article className="flex flex-col" style={{ border: "1px solid rgba(20,19,17,0.12)" }} data-testid="path-rise-within">
            <img src={PHOTOS.bodysuitKneel} alt="RISE WITHIN — 1:1 Coaching" className="w-full aspect-[4/3] object-cover" />
            <div className="p-6 md:p-10 flex-1 flex flex-col">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="lp-label-sm" style={{ color: BRONZE }}>Elite · 1:1 Coaching</span>
                <span className="lp-chip">By Application</span>
              </div>
              <h3 className="lp-h text-4xl mt-5">Rise Within</h3>
              <p className="lp-it text-2xl mt-1" style={{ color: BRONZE }}>fully bespoke.</p>
              <p className="mt-5 leading-relaxed" style={{ color: MUTED }}>
                For the woman who wants me in her corner, weekly. Functional lab testing, fully
                customized protocols, weekly check-ins, and direct access. The elite tier —
                limited spots, by application only.
              </p>
              <ul className="mt-6 space-y-3 flex-1">
                {RISE_BULLETS.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px]" style={{ color: "#4A453D" }}>
                    <Check size={14} className="mt-1 flex-shrink-0" style={{ color: BRONZE }} /> {b}
                  </li>
                ))}
              </ul>
              <Link to="/apply" className="lp-btn lp-btn-fill mt-8 self-start" data-testid="apply-rise-within">
                Apply for 1:1 <ArrowRight size={13} />
              </Link>
            </div>
          </article>

          {/* MTM */}
          <article className="flex flex-col" style={{ border: "1px solid rgba(20,19,17,0.12)" }} data-testid="path-mtm">
            <img src={PHOTOS.athleticYoga} alt="Metabolic Transformation Method" className="w-full aspect-[4/3] object-cover" />
            <div className="p-6 md:p-10 flex-1 flex flex-col">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="lp-label-sm" style={{ color: BRONZE }}>Group · By Application</span>
                <span className="lp-chip">Limited seats</span>
              </div>
              <h3 className="lp-h text-4xl mt-5 leading-tight">Metabolic<br />Transformation<br />Method</h3>
              <p className="lp-it text-2xl mt-1" style={{ color: BRONZE }}>four phases. one revolution.</p>
              <p className="mt-5 leading-relaxed" style={{ color: MUTED }}>
                The exact clinical method my 1:1 clients receive — functional protocols, coaching
                calls, and the full four-phase curriculum — carried by an intimate group of women
                moving through it together. Nothing stripped down. Everything shared.
              </p>
              <ul className="mt-6 space-y-3 flex-1">
                {MTM_BULLETS.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px]" style={{ color: "#4A453D" }}>
                    <Check size={14} className="mt-1 flex-shrink-0" style={{ color: BRONZE }} /> {b}
                  </li>
                ))}
              </ul>

              {/* Investment — inquiry mode */}
              <div className="mt-8 pt-7 border-t" style={{ borderColor: "rgba(20,19,17,0.12)" }} data-testid="mtm-investment">
                <p className="lp-label-sm" style={{ color: BRONZE }}>The Investment</p>
                <p className="lp-it text-xl mt-2" style={{ color: INK }}>Premium coaching. Shared table.</p>
                <p className="text-sm mt-3 leading-relaxed" style={{ color: MUTED }} data-testid="mtm-inquiry-note">
                  Because the method is carried by a group, the investment is <strong style={{ color: "#4A453D" }}>less than
                  half of 1:1</strong> — with nothing removed from the method itself. We'll walk through
                  the details and flexible payment options together on your call.
                </p>
                {seats && seats.remaining > 0 && (
                  <div className="mt-5 flex items-baseline justify-between" data-testid="seats-counter">
                    <span className="lp-label-sm" style={{ color: BRONZE }}>This cohort</span>
                    <span className="lp-label-sm" style={{ color: INK }} data-testid="seats-remaining">
                      Only {seats.remaining} spots left
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link to="/apply" className="lp-btn lp-btn-fill w-full sm:w-auto" data-testid="mtm-inquire-btn">
                  Inquire &amp; Apply <ArrowRight size={13} />
                </Link>
                <a href={BRAND.calendlyUrl} target="_blank" rel="noreferrer" className="lp-btn lp-btn-outline w-full sm:w-auto" data-testid="mtm-book-call-btn">
                  Book a Call
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* ═══ FENYX Membership ═══ */}
      <FenyxSection />

      {/* ═══ Four Phases ═══ */}
      <section className="px-6 md:px-12 py-16 md:py-28" style={{ background: LINEN }} data-testid="phases-section">
        <div className="max-w-[1200px] mx-auto">
          <p className="lp-label-sm" style={{ color: BRONZE }}>Inside the Metabolic Transformation Method</p>
          <h2 className="lp-h mt-5 text-4xl md:text-5xl">
            Four phases.<br />
            <span className="lp-it normal-case" style={{ color: BRONZE }}>One quiet revolution.</span>
          </h2>
          <p className="mt-6 text-lg max-w-2xl leading-relaxed" style={{ color: MUTED }}>
            Six months. The same protocol I take my 1:1 clients through — now in a group container
            that fits stay-at-home moms, shift workers, and corporate women alike.
          </p>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
            {PHASES.map((p) => (
              <div key={p.num} className="p-8 md:p-10" style={{ background: "#FFFFFF", border: "1px solid rgba(20,19,17,0.08)" }} data-testid={`phase-${p.title.toLowerCase()}`}>
                <div className="flex items-baseline justify-between">
                  <span className="lp-label-sm" style={{ color: BRONZE }}>{p.num}</span>
                  <span className="lp-label-sm" style={{ color: MUTED }}>{p.weeks}</span>
                </div>
                <h3 className="lp-h text-3xl mt-4">{p.title}</h3>
                <p className="mt-4 leading-relaxed" style={{ color: MUTED }}>{p.body}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {p.tags.map((t) => <span key={t} className="lp-chip">{t}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Client Wins ═══ */}
      <section id="results" className="px-6 md:px-12 py-16 md:py-28 max-w-[1300px] mx-auto" data-testid="client-wins">
        <div className="text-center">
          <p className="lp-label-sm" style={{ color: BRONZE }}>Client Wins</p>
          <h2 className="lp-h mt-5 text-5xl md:text-6xl">Real women.</h2>
          <p className="lp-it text-3xl md:text-4xl mt-2" style={{ color: BRONZE }}>Real restoration.</p>
          <p className="lp-it mt-8 text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed" style={{ color: MUTED }}>
            Every result below was achieved without extreme measures — no starvation, no hours of
            cardio, no quick fixes. Just the right protocol, in the right order, with the right support.
          </p>
        </div>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="transformation-grid">
          {TRANSFORMATIONS.map((t) => (
            <article key={t.name} style={{ border: "1px solid rgba(20,19,17,0.1)" }} data-testid={`win-${t.name.toLowerCase()}`}>
              <img src={t.src} alt={`${t.name} transformation`} loading="lazy" className="w-full aspect-square object-cover" />
              <div className="p-6">
                <h3 className="lp-h text-2xl">{t.name}</h3>
                <p className="lp-label-sm mt-3" style={{ color: BRONZE }}>Starting Point</p>
                <p className="lp-it text-lg mt-1" style={{ color: MUTED }}>{t.condition}</p>
                <p className="lp-label-sm mt-4" style={{ color: BRONZE }}>The Restoration</p>
                <ul className="mt-2 space-y-1.5">
                  {t.results.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm" style={{ color: "#4A453D" }}>
                      <Check size={12} className="mt-1 flex-shrink-0" style={{ color: BRONZE }} /> {r}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ═══ Qualifications ═══ */}
      <section className="px-6 md:px-12 py-16 md:py-28" style={{ background: "#FAF8F4" }} data-testid="qualifications-section">
        <div className="max-w-[1100px] mx-auto">
          <p className="lp-label-sm" style={{ color: BRONZE }}>Qualifications ·</p>
          <h2 className="lp-h mt-5 text-4xl md:text-5xl normal-case" style={{ textTransform: "none" }}>Training you can trust.</h2>
          <p className="mt-6 text-lg max-w-2xl leading-relaxed" style={{ color: MUTED }}>
            Every recommendation is built on evidence, informed by testing, and delivered with
            clinical precision. Not trends. Not guesswork. Science — applied with care.
          </p>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10">
            {QUALS.map((q, i) => (
              <div key={q.title} className="pt-8 border-t" style={{ borderColor: "rgba(20,19,17,0.14)" }} data-testid={`qual-${i + 1}`}>
                <h3 className="lp-h text-xl normal-case" style={{ textTransform: "none" }}>{q.title}</h3>
                <p className="mt-3 leading-relaxed" style={{ color: MUTED }}>{q.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <FAQSection />

      {/* ═══ Free guide — lead magnet ═══ */}
      <LeadMagnetSection />

      {/* ═══ Closing CTA ═══ */}
      <section className="px-6 md:px-12 py-20 md:py-32 text-center max-w-[900px] mx-auto" data-testid="closing-cta">
        <p className="lp-label-sm" style={{ color: BRONZE }}>Your next step ·</p>
        <h2 className="lp-h mt-6 text-4xl md:text-6xl normal-case" style={{ textTransform: "none" }}>
          Ready to stop guessing<br />
          and start <span className="lp-it" style={{ color: BRONZE }}>healing?</span>
        </h2>
        <div className="mt-12">
          <a href={BRAND.calendlyUrl} target="_blank" rel="noreferrer" className="lp-btn lp-btn-fill" data-testid="footer-cta">
            Book Your Discovery Call
          </a>
        </div>
        <p className="lp-label-sm mt-8" style={{ color: MUTED }}>Free · 20 minutes · No pressure. Just clarity on where to begin.</p>
      </section>

      {/* ═══ Footer ═══ */}
      <footer className="py-20 px-6 md:px-12" style={{ background: "#141311", color: "#D8D2C6" }}>
        <div className="max-w-[1300px] mx-auto flex flex-col items-center text-center gap-6">
          <img src={ASSETS.logo} alt={BRAND.fullName} className="w-32 h-32 md:w-44 md:h-44 object-contain" />
          <p className="lp-label-sm" style={{ color: BRONZE }}>{BRAND.tagline}</p>
          <div className="lp-rule" />
          <p className="lp-label-sm" style={{ color: "#7A6E60" }}>© 2026 · {BRAND.handle}</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" data-testid="footer-privacy" className="lp-label-sm hover:underline" style={{ color: "#7A6E60" }}>Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="lp-label-sm hover:underline" style={{ color: "#7A6E60" }}>Terms</Link>
            <Link to="/login" data-testid="footer-coach-login" className="lp-label-sm hover:underline" style={{ color: "#57503F", fontSize: "9px" }}>
              Coach sign in
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
